import type { PortfolioPublicationKind, Showcase, ShowcaseRequest, SocialNetwork } from "@brandex/api-client";
import type { MessageKey } from "@brandex/i18n";

/**
 * Portfolio highlights as the form edits them: years are text while typed,
 * social links are one box per network. Same limits as the server
 * (`brandex-domain` `Showcase::parse`), so most mistakes show before saving.
 */
export interface ShowcaseDraft {
  tagline: string;
  headline: string;
  since: string;
  services: string[];
  experience: { role: string; organization: string; from: string; to: string; current: boolean }[];
  awards: { title: string; issuer: string; year: string }[];
  memberships: { organization: string; role: string }[];
  publications: { kind: PortfolioPublicationKind; title: string; venue: string; year: string; url: string }[];
  links: Record<SocialNetwork, string>;
  careAreas: { title: string; description: string }[];
  /** Video consultations: offered or not, with an optional note and booking link. */
  online: { offered: boolean; note: string; url: string };
}

export const NETWORKS: readonly SocialNetwork[] = ["facebook", "youtube", "instagram", "linkedin", "x", "tiktok", "website"];
export const PUBLICATION_KINDS: readonly PortfolioPublicationKind[] = ["journal", "book", "media", "talk"];

export const LIMITS = {
  tagline: 60,
  headline: 120,
  careAreas: 6,
  careTitle: 80,
  careDescription: 280,
  onlineNote: 200,
  services: 24,
  service: 80,
  experience: 15,
  awards: 15,
  memberships: 15,
  publications: 30,
} as const;

/** Hosts each network's link must be on (empty: any https site). Mirrors the server. */
export const NETWORK_HOSTS: Record<SocialNetwork, readonly string[]> = {
  facebook: ["facebook.com", "fb.com"],
  youtube: ["youtube.com", "youtu.be"],
  instagram: ["instagram.com"],
  linkedin: ["linkedin.com"],
  x: ["x.com", "twitter.com"],
  tiktok: ["tiktok.com"],
  website: [],
};

const year = (v: number | null | undefined) => (v === null || v === undefined ? "" : String(v));
const num = (v: string): number | null => (/^\d{4}$/.test(v.trim()) ? Number(v.trim()) : null);

export function showcaseToDraft(s: Showcase): ShowcaseDraft {
  // The web app may be deployed before the API that adds a field; read the
  // newer ones as possibly missing so an older API can't break the form.
  const newer = s as Partial<Pick<Showcase, "tagline" | "care_areas" | "online_consultation">>;
  const links = Object.fromEntries(NETWORKS.map((n) => [n, ""])) as Record<SocialNetwork, string>;
  for (const l of s.links) links[l.network] = l.url;
  return {
    tagline: newer.tagline ?? "",
    headline: s.headline,
    since: year(s.practising_since),
    services: [...s.services],
    experience: s.experience.map((e) => ({
      role: e.role,
      organization: e.organization,
      from: String(e.from_year),
      to: year(e.to_year),
      current: e.to_year === null || e.to_year === undefined,
    })),
    awards: s.awards.map((a) => ({ title: a.title, issuer: a.issuer ?? "", year: year(a.year) })),
    memberships: s.memberships.map((m) => ({ organization: m.organization, role: m.role ?? "" })),
    publications: s.publications.map((p) => ({
      kind: p.kind,
      title: p.title,
      venue: p.venue ?? "",
      year: year(p.year),
      url: p.url ?? "",
    })),
    links,
    careAreas: (newer.care_areas ?? []).map((a) => ({ title: a.title, description: a.description ?? "" })),
    online: {
      offered: newer.online_consultation !== null && newer.online_consultation !== undefined,
      note: newer.online_consultation?.note ?? "",
      url: newer.online_consultation?.url ?? "",
    },
  };
}

/** Rows the doctor left completely empty are dropped instead of failing. */
export function toShowcaseRequest(d: ShowcaseDraft): ShowcaseRequest {
  return {
    tagline: d.tagline.trim().replace(/\s+/g, " "),
    headline: d.headline.trim().replace(/\s+/g, " "),
    practising_since: num(d.since),
    services: d.services.map((s) => s.trim()).filter(Boolean),
    experience: d.experience
      .filter((e) => e.role.trim() || e.organization.trim() || e.from.trim())
      .map((e) => ({
        role: e.role.trim(),
        organization: e.organization.trim(),
        from_year: num(e.from) ?? 0,
        to_year: e.current ? null : num(e.to),
      })),
    awards: d.awards
      .filter((a) => a.title.trim() || a.issuer.trim())
      .map((a) => ({ title: a.title.trim(), issuer: a.issuer.trim(), year: num(a.year) })),
    memberships: d.memberships
      .filter((m) => m.organization.trim() || m.role.trim())
      .map((m) => ({ organization: m.organization.trim(), role: m.role.trim() })),
    publications: d.publications
      .filter((p) => p.title.trim() || p.venue.trim() || p.url.trim())
      .map((p) => ({
        kind: p.kind,
        title: p.title.trim(),
        venue: p.venue.trim(),
        year: num(p.year),
        url: p.url.trim() === "" ? null : p.url.trim(),
      })),
    links: NETWORKS.filter((n) => d.links[n].trim() !== "").map((n) => ({ network: n, url: d.links[n].trim() })),
    care_areas: d.careAreas
      .filter((a) => a.title.trim() || a.description.trim())
      .map((a) => ({ title: a.title.trim(), description: a.description.trim() })),
    online_consultation: d.online.offered
      ? { note: d.online.note.trim(), url: d.online.url.trim() === "" ? null : d.online.url.trim() }
      : null,
  };
}

/** `*words*` in a headline are emphasised; markers come in pairs around some text. */
export function emphasisOk(headline: string): boolean {
  const marks = (headline.match(/\*/g) ?? []).length;
  return marks % 2 === 0 && !headline.includes("**");
}

/** Whether `raw` is an https link on one of `hosts` (any host when empty). */
export function isHttpsLink(raw: string, hosts: readonly string[] = []): boolean {
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return false;
  }
  if (url.protocol !== "https:" || url.username || url.password || !url.hostname.includes(".")) return false;
  const host = url.hostname.toLowerCase();
  return hosts.length === 0 || hosts.some((h) => host === h || host.endsWith(`.${h}`));
}

export type ShowcaseErrors = Record<string, MessageKey>;

/** Client-side checks; keys are `field` or `list.index.field`. */
export function validateShowcase(d: ShowcaseDraft, thisYear: number): ShowcaseErrors {
  const errors: ShowcaseErrors = {};
  const len = (v: string, min: number, max: number) => {
    const n = v.trim().length;
    return n >= min && n <= max;
  };
  const yearOk = (v: string, required = false) => {
    if (v.trim() === "") return !required;
    const y = num(v);
    return y !== null && y >= 1950 && y <= thisYear;
  };
  if (!len(d.tagline, 0, LIMITS.tagline)) errors.tagline = "showcase.error.tagline";
  if (!len(d.headline, 0, LIMITS.headline)) errors.headline = "showcase.error.headline";
  else if (!emphasisOk(d.headline)) errors.headline = "showcase.error.emphasis";
  if (!yearOk(d.since)) errors.since = "showcase.error.year";
  const seen = new Set<string>();
  d.services.forEach((s, i) => {
    const key = s.trim().toLowerCase();
    if (!len(s, 2, LIMITS.service)) errors[`services.${i}`] = "showcase.error.service";
    else if (seen.has(key)) errors[`services.${i}`] = "showcase.error.duplicate";
    seen.add(key);
  });
  d.experience.forEach((e, i) => {
    if (!(e.role.trim() || e.organization.trim() || e.from.trim())) return;
    if (!len(e.role, 2, 120)) errors[`experience.${i}.role`] = "showcase.error.required";
    if (!len(e.organization, 2, 160)) errors[`experience.${i}.organization`] = "showcase.error.required";
    if (!yearOk(e.from, true)) errors[`experience.${i}.from`] = "showcase.error.year";
    if (!e.current) {
      if (!yearOk(e.to)) errors[`experience.${i}.to`] = "showcase.error.year";
      else if (num(e.to) !== null && (num(e.to) ?? 0) < (num(e.from) ?? 0)) errors[`experience.${i}.to`] = "showcase.error.order";
    }
  });
  d.awards.forEach((a, i) => {
    if (!(a.title.trim() || a.issuer.trim())) return;
    if (!len(a.title, 2, 160)) errors[`awards.${i}.title`] = "showcase.error.required";
    if (!yearOk(a.year)) errors[`awards.${i}.year`] = "showcase.error.year";
  });
  d.memberships.forEach((m, i) => {
    if (!(m.organization.trim() || m.role.trim())) return;
    if (!len(m.organization, 2, 160)) errors[`memberships.${i}.organization`] = "showcase.error.required";
  });
  d.publications.forEach((p, i) => {
    if (!(p.title.trim() || p.venue.trim() || p.url.trim())) return;
    if (!len(p.title, 2, 200)) errors[`publications.${i}.title`] = "showcase.error.required";
    if (!yearOk(p.year)) errors[`publications.${i}.year`] = "showcase.error.year";
    if (p.url.trim() !== "" && !isHttpsLink(p.url)) errors[`publications.${i}.url`] = "showcase.error.link";
  });
  const areas = new Set<string>();
  d.careAreas.forEach((a, i) => {
    if (!(a.title.trim() || a.description.trim())) return;
    const key = a.title.trim().toLowerCase();
    if (!len(a.title, 2, LIMITS.careTitle)) errors[`careAreas.${i}.title`] = "showcase.error.required";
    else if (areas.has(key)) errors[`careAreas.${i}.title`] = "showcase.error.duplicate";
    areas.add(key);
    if (!len(a.description, 0, LIMITS.careDescription)) errors[`careAreas.${i}.description`] = "showcase.error.tooLong";
  });
  if (d.online.offered) {
    if (!len(d.online.note, 0, LIMITS.onlineNote)) errors["online.note"] = "showcase.error.tooLong";
    if (d.online.url.trim() !== "" && !isHttpsLink(d.online.url)) errors["online.url"] = "showcase.error.link";
  }
  for (const n of NETWORKS) {
    const v = d.links[n].trim();
    if (v !== "" && !isHttpsLink(v, NETWORK_HOSTS[n])) errors[`links.${n}`] = "showcase.error.network";
  }
  return errors;
}

/** "16 years" style number for the site, or null without a start year. */
export function yearsInPractice(since: number | null | undefined, now: Date): number | null {
  if (since === null || since === undefined) return null;
  return Math.max(0, now.getFullYear() - since);
}
